import { useEffect, useState, useMemo } from 'react'
import { useAtom } from 'jotai'

import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Box, Text, Link, VStack, Image, Center } from '@chakra-ui/react'

import { bookDetailsApi } from '@!/Api/storeBook'
import { bookDetailsKeys } from '@!/Api/queryKeys'
import { useSearchCard, useAppCookies, usePurchase, usePointBackMeasure, useCartItems, useCalculatePointBack, useNavigationHandler } from '@!/Hooks'

import { savePreviousPaymentMethod } from '@!/Util/localStorageData'

import PurchaseSummary from '@!/Store/Common/components/PurchaseSummary'
import CampaignPointSummary from '@!/Store/Common/components/CampaignPointSummary'
import PaymentMethod from '@!/Store/Purchase/components/PaymentMethod'
import PointUsageForm from '@!/Store/Purchase/components/PointUsageForm'
import CouponForm from '@!/Store/Purchase/components/CouponForm'
import PurchaseDetails from '@!/Store/Purchase/components/PurchaseDetails'
import { ConfirmPopupModal, CircularProgressModal } from '@!/components/Modal'
import { LessCoinsPopupModal } from '@!/Store/Purchase/components'
import MonthlyCourseErrorCancelledDialogContainer from '@!/Store/Purchase/containers/MonthlyCourseErrorCancelledDialogContainer'
import { PrimaryButton } from '@!/components/Button'
import { userHashAtom, userUsableCoinsAtom, userPointsAtom } from '@!/atoms/userInfoAtom'
import { cardInfoAtom } from '@!/atoms/paymentAtom'
import { calculatePurchasePrices } from '@!/Store/Purchase/utils/calculatePurchasePrices'
import { isBlank } from '@!/Util'
import { buildAssetUrlString } from '@!/Util'
import monthlyCourseBanner from '@images/monthly_course_bonus_60_banner.webp'
import bgBanner from '@images/bg_banner.webp'

const BookPurchase = ({ bookId, monthlyErrorCancelledCourses }) => {
  const navigate = useNavigate()
  const [userUsableCoins] = useAtom(userUsableCoinsAtom)
  const [userPoints] = useAtom(userPointsAtom)
  const [userHash] = useAtom(userHashAtom)
  const { removeCoinCountsCookie, removePointCountsCookie } = useAppCookies()
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)
  const [books, setBooks] = useState([])
  const [spendPoints, setSpendPoints] = useState(false)
  const [pointValue, setPointValue] = useState('')
  const [openPopupType, setOpenPopupType] = useState('')
  const [alertMessage, setAlertMessage] = useState('')
  const [isCouponApplied, setIsCouponApplied] = useState(false)
  const [currentCoupon, setCurrentCoupon] = useState({})
  const { basePrice, couponAppliedPrice, totalPrice, taxExcludedPrice } = calculatePurchasePrices(books, isCouponApplied, currentCoupon, pointValue)

  const { data: pointBackMeasure, isError: pointBackMeasureError, isLoading: pointBackMeasureLoading } = usePointBackMeasure()
  const [cardInfo] = useAtom(cardInfoAtom)
  const [searchCardMutation, cardInfoError] = useSearchCard(userHash)

  const hasCardInfo = useMemo(() => !isBlank(cardInfo), [cardInfo])
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('coin')

  const { data: cartItems, error: cartItemsError, isLoading: cartItemsLoading } = useCartItems(userHash)
  const {
    data: bookDetails,
    error: bookDetailsError,
    isLoading: bookDetailsLoading,
  } = useQuery({
    queryKey: bookDetailsKeys.bookDetails(bookId),
    queryFn: () => bookDetailsApi(bookId),
    enabled: !!bookId,
    // https://github.com/AmaziaInc/MangaBANG-WEB/issues/2417
    // 以下、自動fetch防止のための設定 (価格更新を避けたい)
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  })

  const previousPage = bookId ? 'bookDetailPage' : 'cartPage'
  const [purchaseMutation, redirectUrl, apiError] = usePurchase(previousPage)

  const pointBackWithoutCoupon = useCalculatePointBack(books, pointBackMeasure, pointValue, isCouponApplied)

  const pointBack = useMemo(() => {
    if (isCouponApplied) {
      // クーポン利用の場合、還元施策対象外の作品があるかは考慮しない
      // 割引き後の税抜き価格 x 還元率
      return Math.floor(taxExcludedPrice * (pointBackMeasure.point_back_rate / 100))
    }

    // クーポン利用がない場合
    return pointBackWithoutCoupon
  }, [isCouponApplied, taxExcludedPrice, pointBackMeasure, pointBackWithoutCoupon])

  const openPurChaseErrorPopup = (message) => {
    setAlertMessage(message)
    setOpenPopupType('purchaseError')
  }

  const { redirectToLoginWithToast } = useNavigationHandler()

  useEffect(() => {
    if (userHash === undefined) {
      redirectToLoginWithToast()
    }
  }, [userHash])

  useEffect(() => {
    setIsLoading(pointBackMeasureLoading || searchCardMutation.isPending || cartItemsLoading || bookDetailsLoading)

    let errors = []
    if (pointBackMeasureError) {
      errors.push(pointBackMeasureError)
    }
    if (cardInfoError) {
      errors.push(cardInfoError)
    }
    if (cartItemsError) {
      errors.push(cartItemsError)
    }
    if (bookDetailsError) {
      errors.push(bookDetailsError)
    }

    if (!isBlank(errors)) {
      setError(errors)
    }
  }, [pointBackMeasure, searchCardMutation.isPending, cartItemsLoading, bookDetailsLoading])

  useEffect(() => {
    if (bookId) {
      setBooks(bookDetails)
    } else {
      setBooks(cartItems)
    }
  }, [bookId, bookDetails, cartItems])

  useEffect(() => {
    if (purchaseMutation.isSuccess && redirectUrl) {
      savePreviousPaymentMethod(selectedPaymentMethod)
      removeCoinCountsCookie()
      removePointCountsCookie()

      window.location.href = redirectUrl
      purchaseMutation.reset()
    }
  }, [purchaseMutation.isSuccess, redirectUrl])

  useEffect(() => {
    if (!apiError) return

    if (redirectUrl) {
      window.location.href = redirectUrl
      purchaseMutation.reset()
    } else {
      openPurChaseErrorPopup(apiError)
    }
  }, [apiError])

  if (error) {
    return <div>Error</div>
  }

  const pageBack = (event) => {
    event.preventDefault()
    navigate(-1)
  }

  const handleGoCoinPurchase = () => {
    window.location.href = '/user/payment'
  }

  const openAlertPopup = (message) => {
    setAlertMessage(message)
    setOpenPopupType('alert')
  }

  const closePopup = () => {
    setOpenPopupType('')
  }

  const openLessCoinsPopup = () => {
    setOpenPopupType('lessCoins')
  }

  const handlePurchase = async (e) => {
    e.preventDefault()

    if (selectedPaymentMethod === 'coin' && userUsableCoins < totalPrice) {
      openLessCoinsPopup()
      return
    }

    if (selectedPaymentMethod === 'credit' && isBlank(cardInfo)) {
      openAlertPopup('クレジットカード情報を登録してください。')
      return
    }

    if (spendPoints) {
      if (pointValue === '' || Number(pointValue) === 0) {
        openAlertPopup('ポイントを入力してください。')
        return
      }
      if (Number(pointValue) > userPoints) {
        openAlertPopup('ポイントが足りません。')
        return
      }
    }

    const paymentType = totalPrice === 0 ? 'none' : selectedPaymentMethod

    const purchaseParams = {
      payment_type: paymentType,
      use_point_count: pointValue,
      total_price: totalPrice,
      purchase_details: books,
      point_back: pointBack,
    }

    if (isCouponApplied) {
      purchaseParams.coupon_code = currentCoupon.code
    }

    purchaseMutation.mutate(purchaseParams)
  }

  const closeErrorPopup = () => {
    console.log(error)
    setError(null)
  }

  return (
    <>
      {isLoading ? (
        <CircularProgressModal isOpen={isLoading} />
      ) : error ? (
        <ConfirmPopupModal isOpen={!!error} text="エラーが発生しました。" handlePopup={closeErrorPopup} />
      ) : (
        <Box fontSize="xs" p={4} bg="#f3f3f8" maxW="800px" mx="auto">
          <Box p={4} bg="white" borderWidth={1} borderRadius="md">
            <PurchaseSummary totalPrice={totalPrice} basePrice={basePrice} isCouponApplied={isCouponApplied} />
            {!isBlank(pointBackMeasure) && <CampaignPointSummary pointBack={pointBack} pointBackRate={pointBackMeasure.point_back_rate} />}
          </Box>
          <Text fontSize="md" fontWeight="bold" color="black" py={3}>
            決済手段
          </Text>
          <Link href="/user/payment#/monthly_course">
            <Box h="37px" w="100%" mb={3} position="relative" borderRadius="base" backgroundImage={`url(${buildAssetUrlString(bgBanner)})`}>
              <Center position="absolute" top="0" left="0" h="100%" w="100%" py="6px">
                <Image src={buildAssetUrlString(monthlyCourseBanner)} alt="月額コース登録ならポイント還元最大60%" h="100%" />
              </Center>
            </Box>
          </Link>
          <MonthlyCourseErrorCancelledDialogContainer monthlyErrorCancelledCourses={monthlyErrorCancelledCourses} />
          <PaymentMethod
            hasCardInfo={hasCardInfo}
            cardInfo={cardInfo}
            selectedPaymentMethod={selectedPaymentMethod}
            setSelectedPaymentMethod={setSelectedPaymentMethod}
            isDisabled={totalPrice === 0}
          />
          <Text fontSize="md" fontWeight="bold" color="black" py={3}>
            クーポン
          </Text>
          <Box p={4} bg="white" borderWidth={1} borderRadius="md">
            <CouponForm
              setIsCouponApplied={setIsCouponApplied}
              setCurrentCoupon={setCurrentCoupon}
              setPointValue={setPointValue}
              setSpendPoints={setSpendPoints}
              basePrice={basePrice}
            />
          </Box>
          <Text fontSize="md" fontWeight="bold" color="black" py={3}>
            ポイント利用
          </Text>
          <Box p={4} bg="white" borderWidth={1} borderRadius="md">
            <PointUsageForm
              points={userPoints}
              pointValue={pointValue}
              setPointValue={setPointValue}
              spendPoints={spendPoints}
              setSpendPoints={setSpendPoints}
              currentPrice={couponAppliedPrice || basePrice}
              isDisabled={basePrice === 0 || couponAppliedPrice === 0}
            />
          </Box>
          <Text fontSize="md" fontWeight="bold" color="black" py={3}>
            購入明細
          </Text>
          <Box p={4} bg="white" borderWidth={1} borderRadius="md">
            <PurchaseDetails books={books} />
          </Box>
          <Text fontSize="md" fontWeight="bold" color="black" py={3}></Text>
          <Box p={4} bg="white" borderWidth={1} borderRadius="md">
            <PurchaseSummary totalPrice={totalPrice} basePrice={basePrice} isCouponApplied={isCouponApplied} />
            {!isBlank(pointBackMeasure) && <CampaignPointSummary pointBack={pointBack} pointBackRate={pointBackMeasure.point_back_rate} />}
            <Box textAlign="center" mt={3}>
              <PrimaryButton onClick={handlePurchase} isDisabled={purchaseMutation.isPending} width="100%">
                決済を完了する
              </PrimaryButton>
            </Box>
          </Box>
          <VStack my={3} spacing={0}>
            <Link href="/static_page/specified_commercial_transaction" isExternal mb="1em">
              特定商取引法の表示
            </Link>
            <Link href="#" onClick={pageBack}>
              {'前のページへ戻る'}
            </Link>
          </VStack>
          <ConfirmPopupModal isOpen={openPopupType === 'alert'} text={alertMessage} handlePopup={closePopup} />
          <ConfirmPopupModal isOpen={openPopupType === 'purchaseError'} text={alertMessage} handlePopup={closePopup} />
          <LessCoinsPopupModal isOpen={openPopupType === 'lessCoins'} handleGoCoinPurchase={handleGoCoinPurchase} handleClose={closePopup} />
          <CircularProgressModal isOpen={purchaseMutation.isPending} />
        </Box>
      )}
    </>
  )
}

export default BookPurchase
